// src/hooks/useShortcuts.js
// Gmail-style single-key shortcuts (3.47.0). 3.47.3: / is described as what it
// does (jumps to the page's search box, where the page has one) and says so
// when the page has none. Never while typing in a box or
// with Ctrl / ⌘ / Alt held, and not behind an open pop-up (except ?). On
// unless turned off in Preferences (prefs.shortcuts).
//   /        jump to this page's search box (e.g. Find a fax on the Overview)
//   N        new fax (Compose)
//   G then O Overview · G then C Compose · G then X Outbox · G then I Inbox
//   ?        the list of shortcuts
//   Esc      closes pop-ups (each pop-up handles it)
import { useEffect, useRef } from 'react';

export const SHORTCUTS = [
  { keys: ['/'], what: 'Jump to the search box, on pages that have one (like Find a fax on the Overview, or Search the Library)' },
  { keys: ['N'], what: 'New fax' },
  { keys: ['G', 'O'], what: 'Go to the Overview' },
  { keys: ['G', 'C'], what: 'Go to Compose' },
  { keys: ['G', 'X'], what: 'Go to the Outbox' },
  { keys: ['G', 'I'], what: 'Go to the Inbox' },
  { keys: ['?'], what: 'Show these shortcuts' },
  { keys: ['Esc'], what: 'Close a pop-up or menu' },
];
const GO = { o: 'dashboard', c: 'compose', x: 'outgoing', i: 'incoming' };
const GO_WINDOW_MS = 1500;

function typing(el) {
  if (!el || el === document.body) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color'].includes(el.type);
  return false;
}
const popupOpen = () => !!document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]');

// The page's search box: the Overview's Find a fax, else a box in the page
// whose placeholder says Search or Find (Library, Users, Contacts, Clients…)
function searchBox() {
  const main = document.querySelector('main') || document;
  return main.querySelector('input.ov-search')
    || [...main.querySelectorAll('input[type="search"], input[placeholder]')].find(i => i.offsetParent !== null && (i.type === 'search' || /^\s*(search|find)\b/i.test(i.placeholder)))
    || null;
}

export function useShortcuts({ enabled, goToTab, canOpenTab, onHelp, notify }) {
  const ref = useRef({ goToTab, canOpenTab, onHelp, notify });
  ref.current = { goToTab, canOpenTab, onHelp, notify };
  useEffect(() => {
    if (!enabled) return undefined;
    let gAt = 0;
    function onKey(e) {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (typing(e.target)) return;
      const { goToTab: go, canOpenTab: can, onHelp: help, notify: say } = ref.current;
      const k = e.key;
      if (k === '?') { if (!popupOpen()) { e.preventDefault(); help(); } return; }
      if (popupOpen()) return;
      const low = k.length === 1 ? k.toLowerCase() : k;
      // 3.47.3: a shortcut key leaves nothing focused, so no ring lingers on a
      // menu item clicked earlier (pressing a key makes the browser show it)
      const drop = () => { const a = document.activeElement; if (a && a !== document.body && !typing(a)) a.blur(); };
      if (gAt && Date.now() - gAt < GO_WINDOW_MS && GO[low]) {
        gAt = 0;
        if (can(GO[low])) { e.preventDefault(); drop(); go(GO[low]); }
        return;
      }
      gAt = 0;
      if (low === 'g' && !e.shiftKey) { gAt = Date.now(); drop(); return; }
      if (k === '/') {
        e.preventDefault();
        const box = searchBox();
        if (box) { box.focus(); box.select?.(); }
        else { drop(); say?.({ tone: 'info', icon: '🔍', title: 'No search box on this page', detail: 'The / key jumps to a page\'s search box, like Find a fax on the Overview or Search the Library.' }); }
        return;
      }
      if (low === 'n' && can('compose')) { e.preventDefault(); drop(); go('compose'); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}
